import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLivePageContent } from '@/hooks/usePageContent';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { PublicLayout } from '@/components/public/PublicLayout';
import { Reveal, RevealGroup, motion, fadeUp } from '@/components/public/anim';
import { Seo } from '@/components/Seo';
import { Leaf, Mountain, Sparkles, Users, ArrowRight, Compass } from 'lucide-react';

const SLUG = 'chi-siamo';
const ABOUT_HERO = 'https://images.pexels.com/photos/28127038/pexels-photo-28127038.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1920';
const ABOUT_SIDE = 'https://images.pexels.com/photos/8006229/pexels-photo-8006229.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200';
const VALUE_ICONS = [Mountain, Leaf, Sparkles, Users];

const str = (v) => (typeof v === 'string' || typeof v === 'number' ? String(v) : '');

export default function About() {
  const { t } = useTranslation();
  const ov = useLivePageContent(SLUG);

  // Default dai testi attuali (i18n); l'admin li sovrascrive dal CRM senza perdere nulla.
  const dValues = t('pages.about.values', { returnObjects: true }) || [];
  const dStats = t('pages.about.stats', { returnObjects: true }) || [];

  const eyebrow = str(ov.eyebrow) || t('pages.about.eyebrow');
  const title = str(ov.title) || t('pages.about.title');
  const lead = str(ov.lead) || t('pages.about.lead');
  const heroImage = str(ov.heroImage) || ABOUT_HERO;
  const missionTitle = str(ov.introTitle) || t('pages.about.missionTitle');
  const missionText = str(ov.intro) || t('pages.about.missionText');
  const missionImage = str(ov.introImage) || ABOUT_SIDE;
  const valuesTitle = t('pages.about.valuesTitle');
  const values = (Array.isArray(ov.features) && ov.features.length)
    ? ov.features.map((f) => ({ title: str(f.title), text: str(f.desc || f.text) }))
    : dValues;
  const stats = (Array.isArray(ov.stats) && ov.stats.length)
    ? ov.stats.map((s) => ({ value: str(s.value), label: str(s.label) }))
    : dStats;
  const ctaTitle = str(ov.ctaTitle) || t('pages.about.ctaTitle');
  const ctaText = str(ov.ctaText) || t('pages.about.ctaText');
  const ctaBtn = str(ov.ctaLabel) || t('pages.about.ctaBtn');
  const ctaTo = str(ov.ctaTo) || '/partner/registrati';

  return (
    <PublicLayout>
      <Seo title={eyebrow} description={lead} path="/chi-siamo" image={heroImage} />
      {/* Hero */}
      <section className="relative isolate flex min-h-[62vh] items-end overflow-hidden text-white" data-testid="about-hero">
        <div className="absolute inset-0 z-0">
          <img loading="lazy" src={heroImage} alt="Sila" className="h-full w-full object-cover" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, hsla(210,32%,6%,0.45) 0%, hsla(210,32%,6%,0.55) 55%, hsl(var(--background)) 100%)' }} />
        </div>
        <div className="sila-section relative z-10 pb-16 pt-32">
          <motion.span initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide backdrop-blur-md">
            <Compass className="h-3.5 w-3.5" style={{ color: 'hsl(var(--brand-sun))' }} />{eyebrow}
          </motion.span>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-5 max-w-3xl font-display text-4xl font-bold leading-[1.05] tracking-tight drop-shadow-[0_2px_18px_rgba(0,0,0,0.45)] sm:text-6xl">{title}</motion.h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.25 }}
            className="mt-5 max-w-xl text-lg text-white/85">{lead}</motion.p>
        </div>
      </section>

      {/* Mission */}
      <section className="sila-section py-16 sm:py-24">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--brand-sun)/0.22)] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[hsl(var(--brand-bark))]"><Leaf className="h-3.5 w-3.5" />{missionTitle}</span>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-4xl">{missionTitle}</h2>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">{missionText}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="grain-overlay overflow-hidden rounded-[28px] shadow-[var(--shadow-soft)]">
              <img loading="lazy" src={missionImage} alt="Borgo silano" className="h-[420px] w-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Values */}
      <section className="sila-section pb-8">
        <Reveal><h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">{valuesTitle}</h2></Reveal>
        <RevealGroup className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {values.map((v, i) => {
            const Icon = VALUE_ICONS[i] || Leaf;
            return (
              <motion.div key={i} variants={fadeUp}>
                <Card className="h-full rounded-[24px] border-border bg-card shadow-[var(--shadow-sm)] transition-transform duration-300 hover:-translate-y-1" data-testid={`about-value-${i}`}>
                  <CardContent className="p-6">
                    <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/12 text-primary"><Icon className="h-6 w-6" /></span>
                    <h3 className="mt-4 font-display text-xl font-semibold">{v.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{v.text}</p>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </RevealGroup>
      </section>

      {/* Stats band */}
      <section className="sila-section py-16 sm:py-24">
        <div className="grid grid-cols-2 gap-6 rounded-[28px] bg-secondary px-6 py-12 text-center sm:grid-cols-4">
          {stats.map((s, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <div className="font-display text-4xl font-bold text-primary">{s.value}</div>
              <div className="mt-1 text-sm text-muted-foreground">{s.label}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="sila-section pb-24">
        <Reveal>
          <div className="grain-overlay relative overflow-hidden rounded-[32px] bg-primary px-6 py-14 text-center text-primary-foreground sm:px-14">
            <div className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-[hsl(var(--brand-sun)/0.25)] blur-2xl" />
            <h2 className="relative font-display text-3xl font-bold tracking-tight sm:text-4xl">{ctaTitle}</h2>
            <p className="relative mx-auto mt-3 max-w-xl text-primary-foreground/85">{ctaText}</p>
            <Button asChild size="lg" variant="secondary" className="relative mt-7 rounded-full" data-testid="about-cta-button">
              <Link to={ctaTo}>{ctaBtn}<ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>
        </Reveal>
      </section>
    </PublicLayout>
  );
}
